import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Activity, Building2, CheckCircle2, AlertTriangle, Clock, Users, Ticket,
  Zap, TrendingUp, TrendingDown, Loader2, RefreshCw, Bell
} from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StoreCard({ store }) {
  const rate = store.checklist_rate;
  const rateColor = rate >= 80 ? 'text-green-600' : rate >= 50 ? 'text-amber-600' : 'text-red-600';
  const rateBg = rate >= 80 ? 'bg-green-50 border-green-200' : rate >= 50 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';

  return (
    <Card className={`border transition-all hover:shadow-md ${store.checklist_count > 0 ? rateBg : 'border-slate-200 bg-white'}`} data-testid={`store-card-${store.store_id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-bold text-slate-900 text-sm">{store.store_name}</h3>
            {store.city && <p className="text-xs text-slate-500">{store.city}</p>}
          </div>
          <div className={`text-2xl font-bold ${store.checklist_count > 0 ? rateColor : 'text-slate-300'}`}>
            {store.checklist_count > 0 ? `${rate}%` : '--'}
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full h-2 bg-slate-100 rounded-full mb-3 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${rate >= 80 ? 'bg-green-500' : rate >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
            style={{ width: `${store.checklist_count > 0 ? rate : 0}%` }}
          />
        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-white/60 rounded-lg p-2">
            <p className="text-sm font-bold text-slate-700">{store.checklist_completed}/{store.checklist_total}</p>
            <p className="text-[10px] text-slate-500">Tasks Done</p>
          </div>
          <div className="bg-white/60 rounded-lg p-2">
            <p className="text-sm font-bold text-orange-600">{store.open_tickets}</p>
            <p className="text-[10px] text-slate-500">Tickets</p>
          </div>
          <div className="bg-white/60 rounded-lg p-2">
            <p className="text-sm font-bold text-purple-600">{store.pending_tasks}</p>
            <p className="text-[10px] text-slate-500">Tasks</p>
          </div>
        </div>

        {store.latest_kpi && (
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
            <span className="text-slate-500">Sales: ${store.latest_kpi.net_sales?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            <span className={`font-medium flex items-center gap-1 ${store.latest_kpi.sales_growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {store.latest_kpi.sales_growth >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {store.latest_kpi.sales_growth}%
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function LiveDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { permission, supported, subscribe } = usePushNotifications();

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);
      const res = await axios.get(`${API_URL}/dashboard/live`);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleEnableNotifications = async () => {
    const ok = await subscribe();
    if (ok) toast.success('Push notifications enabled!');
    else toast.error('Could not enable notifications. Check browser permissions.');
  };

  if (!['owner', 'area_manager'].includes(user?.role)) {
    return (
      <div className="text-center py-12">
        <Activity className="w-12 h-12 mx-auto mb-4 text-slate-300" />
        <h2 className="text-lg font-bold text-slate-900 mb-2">Access Restricted</h2>
        <p className="text-slate-600">Only owners can view the live dashboard</p>
      </div>
    );
  }

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-10 h-10 animate-spin text-orange-500" />
    </div>
  );

  const g = data?.global;
  const stores = data?.stores || [];

  return (
    <div className="space-y-6" data-testid="live-dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Live Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">Real-time store performance &middot; {g?.date}</p>
        </div>
        <div className="flex gap-2">
          {supported && permission !== 'granted' && (
            <Button onClick={handleEnableNotifications} variant="outline" size="sm" className="rounded-xl h-9 text-[13px]" data-testid="enable-notifications-btn">
              <Bell className="w-4 h-4 mr-1.5" /> Enable Alerts
            </Button>
          )}
          <Button onClick={() => fetchData(true)} variant="outline" size="sm" className="rounded-xl h-9 text-[13px]" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Push notification banner */}
      {supported && permission === 'granted' && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-sm text-green-700">
          <Bell className="w-4 h-4" /> Push notifications are enabled. You'll receive alerts for tickets, checklists, and tasks.
        </div>
      )}

      {/* Global Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Card className="border border-slate-200/80 rounded-2xl"><CardContent className="p-4 text-center">
          <Building2 className="w-5 h-5 mx-auto mb-1.5 text-orange-500" />
          <p className="text-xl font-bold text-slate-900 tabular-nums">{g?.total_stores}</p>
          <p className="text-[11px] text-slate-500">Stores</p>
        </CardContent></Card>
        <Card className="border border-slate-200/80 rounded-2xl"><CardContent className="p-4 text-center">
          <Users className="w-5 h-5 mx-auto mb-1.5 text-blue-500" />
          <p className="text-xl font-bold text-slate-900 tabular-nums">{g?.total_users}</p>
          <p className="text-[11px] text-slate-500">Users</p>
        </CardContent></Card>
        <Card className="border border-slate-200/80 rounded-2xl"><CardContent className="p-4 text-center">
          <Ticket className="w-5 h-5 mx-auto mb-1.5 text-red-500" />
          <p className="text-xl font-bold text-slate-900 tabular-nums">{g?.total_tickets_open}</p>
          <p className="text-[11px] text-slate-500">Open Tickets</p>
        </CardContent></Card>
        <Card className="border border-slate-200/80 rounded-2xl"><CardContent className="p-4 text-center">
          <Zap className="w-5 h-5 mx-auto mb-1.5 text-violet-500" />
          <p className="text-xl font-bold text-slate-900 tabular-nums">{g?.total_pending_tasks}</p>
          <p className="text-[11px] text-slate-500">Tasks</p>
        </CardContent></Card>
        <Card className="border border-slate-200/80 rounded-2xl"><CardContent className="p-4 text-center">
          <AlertTriangle className="w-5 h-5 mx-auto mb-1.5 text-amber-500" />
          <p className="text-xl font-bold text-slate-900 tabular-nums">{g?.pending_approvals}</p>
          <p className="text-[11px] text-slate-500">Approvals</p>
        </CardContent></Card>
      </div>

      {/* Store Grid */}
      <div>
        <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          <CheckCircle2 className="w-5 h-5 text-orange-500" /> Store Performance Today
        </h2>
        {stores.length === 0 ? (
          <Card className="border-slate-200"><CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">No stores found</p>
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stores.map(store => <StoreCard key={store.store_id} store={store} />)}
          </div>
        )}
      </div>
    </div>
  );
}
